import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./crmPermissions.module.css";

const CrmPermissionCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    route: "",
    category_id: ""
  });
  const [errors, setErrors] = useState({});

  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/permissions/create`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.categories) {
          setCategories(data.data.categories);
        }
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback categories
      setCategories([
        { id: 1, name: "Users" },
        { id: 2, name: "Guardians" },
        { id: 3, name: "Finance" },
        { id: 4, name: "Properties" },
        { id: 5, name: "Clients" },
      ]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
    }

    if (!formData.display_name || formData.display_name.trim() === "") {
      newErrors.display_name = "Display name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${baseUrl}/api/permissions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          display_name: formData.display_name,
          description: formData.description,
          route: formData.route,
          category_id: formData.category_id || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(data.message || 'Permission created successfully!');
          navigate('/permissions');
        } else {
          toast.error(data.message || 'Failed to create permission');
        }
      } else {
        toast.error('Failed to create permission');
      }
    } catch (error) {
      console.error('Error creating permission:', error);
      toast.error('An error occurred while creating permission');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <ol className={styles.breadcrumb}>
            <li><a href="/dashboard">Dashboard</a></li>
            <li><a href="/permissions">Permissions</a></li>
            <li className={styles.active}>Create Permission</li>
          </ol>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          {/* Display Errors */}
          {Object.keys(errors).length > 0 && (
            <div className="alert alert-danger">
              <strong>Whoops!</strong> There were some problems with your input.
              <br /><br />
              <ul>
                {Object.values(errors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Form Panel */}
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
            <div className={`${styles.panelHeading} ${styles.panelTitle}`}>
              <h3>Add permission</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit}>
                <div className={styles.colMd12}>
                  <div className={styles.colMd6}>
                    <dl className={styles.dlHorizontal}>
                      <dt>Name <span style={{ color: 'red' }}>*</span></dt>
                      <dd>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={`${styles.formControl} ${errors.name ? styles.hasError : ''}`}
                          placeholder="e.g., users.manage"
                        />
                      </dd>

                      <dt>Display name <span style={{ color: 'red' }}>*</span></dt>
                      <dd>
                        <input
                          type="text"
                          name="display_name"
                          value={formData.display_name}
                          onChange={handleChange}
                          className={`${styles.formControl} ${errors.display_name ? styles.hasError : ''}`}
                          placeholder="e.g., Manage Users"
                        />
                      </dd>

                      <dt>Description</dt>
                      <dd>
                        <input
                          type="text"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          className={styles.formControl}
                          placeholder="Permission description"
                        />
                      </dd>

                      <dt>Route</dt>
                      <dd>
                        <input
                          type="text"
                          name="route"
                          value={formData.route}
                          onChange={handleChange}
                          className={styles.formControl}
                          placeholder="e.g., users"
                        />
                      </dd>

                      <dt>Category</dt>
                      <dd>
                        <select
                          name="category_id"
                          value={formData.category_id}
                          onChange={handleChange}
                          className={styles.formControl}
                        >
                          <option value="">Select a category (optional)</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </dd>
                    </dl>
                  </div>
                </div>

                <div className={`${styles.formGroup} ${styles.pullRight}`}>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnDefault} ${styles.btnStandard}`}
                    onClick={() => navigate('/permissions')}
                    style={{ marginRight: '10px' }}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`${styles.btn} ${styles.btnPrimary} ${styles.btnStandard}`}
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmPermissionCreate;
